import mapboxgl, { Map } from 'mapbox-gl';
import { Site, LocationAdminDetail } from 'src/api/Api';
import MapboxFeature from 'src/mapbox-helpers/MapboxFeature';
import { mapBoxConfig } from 'src/mapbox-helpers/config';
import { roundToTwoDecimalPlaces } from 'src/util/util';
import GeoJSON from 'geojson';

export interface AddSiteBoundaryOptions {
  onMouseEnter?: (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined; }) => void;
  onMouseLeave?: (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined; }) => void;
}

export const addSiteBoundaryWithColor = (
  map: Map,
  site: Site,
  color: string,
  options?: AddSiteBoundaryOptions
) => {
  if (!site.boundary) return;

  const polygon = new MapboxFeature();
  const sourceId = `site-boundary-${site.site_id}`;
  polygon.add(site.boundary, {
    Name: site.site_name,
    Area: `${roundToTwoDecimalPlaces(site.area)}`,
  });

  if (map.getSource(sourceId)) {
    map.removeSource(sourceId);
  }
  map.addSource(sourceId, { type: 'geojson', data: polygon.toJSON() });

  const fillLayerId = `fill-${sourceId}`;
  if (map.getLayer(fillLayerId)) {
    map.removeLayer(fillLayerId);
  }
  map.addLayer({
    id: fillLayerId,
    type: 'fill',
    source: sourceId,
    paint: {
      'fill-color': color,
      'fill-opacity': mapBoxConfig.polygon.defaultPolygonOpacity,
    },
    filter: ['==', '$type', 'Polygon'],
  });

  if (options?.onMouseEnter)
    map.on('mouseenter', fillLayerId, options.onMouseEnter);
  if (options?.onMouseLeave)
    map.on('mouseleave', fillLayerId, options.onMouseLeave);
};

export const addLocationMarkers = (map: Map, locations: LocationAdminDetail[]) => {
  if (!locations || locations.length === 0) return;

  const features = locations.map(location => {
    if (!location.location) return null;
    const feature = MapboxFeature.parseFeatureFromString(location.location, {
      name: location.location_name,
      id: location.location_id,
    });
    return feature;
  }).filter(f => f !== null) as GeoJSON.Feature<GeoJSON.Point>[];

  if (features.length === 0) return;

  const sourceId = 'location-markers';
  const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
  if (source) {
    source.setData({
      type: 'FeatureCollection',
      features: features,
    });
  } else {
    map.addSource(sourceId, {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: features,
      },
    });
  }

  const layerId = 'location-markers-circle';
  if (!map.getLayer(layerId)) {
    map.addLayer({
      id: layerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-radius': 6,
        'circle-color': '#FFA500', // Orange
        'circle-stroke-width': 1,
        'circle-stroke-color': '#FFFFFF',
      },
    });
  }
};
